import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BrandingProvider } from "@/context/BrandingContext";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Projects from "@/pages/Projects";
import Production from "@/pages/Production";
import Equipment from "@/pages/Equipment";
import WorkOrders from "@/pages/WorkOrders";
import Breakdowns from "@/pages/Breakdowns";
import Lubrication from "@/pages/Lubrication";
import Inventory from "@/pages/Inventory";
import Safety from "@/pages/Safety";
import Reports from "@/pages/Reports";
import CustomerPortal from "@/pages/CustomerPortal";
import Users from "@/pages/Users";
import Documents from "@/pages/Documents";
import Quality from "@/pages/Quality";
import AuditTrail from "@/pages/AuditTrail";
import Energy from "@/pages/Energy";
import Vendors from "@/pages/Vendors";
import Settings from "@/pages/Settings";
import Permits from "@/pages/Permits";
import Notifications from "@/pages/Notifications";
import MasterHome from "@/pages/MasterHome";
import MasterList from "@/pages/MasterList";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role) && user.role !== "super_admin")
    return <Navigate to="/" replace />;
  return children;
}

function RoleRouter() {
  const { user } = useAuth();
  if (user?.role === "customer") return <Navigate to="/customer" replace />;
  return <Dashboard />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Protected><RoleRouter /></Protected>} />
      <Route path="/projects" element={<Protected roles={["super_admin","company_admin","site_manager"]}><Projects /></Protected>} />
      <Route path="/production" element={<Protected roles={["super_admin","company_admin","site_manager","shift_supervisor"]}><Production /></Protected>} />
      <Route path="/equipment" element={<Protected><Equipment /></Protected>} />
      <Route path="/workorders" element={<Protected><WorkOrders /></Protected>} />
      <Route path="/breakdowns" element={<Protected><Breakdowns /></Protected>} />
      <Route path="/lubrication" element={<Protected><Lubrication /></Protected>} />
      <Route path="/inventory" element={<Protected><Inventory /></Protected>} />
      <Route path="/safety" element={<Protected><Safety /></Protected>} />
      <Route path="/permits" element={<Protected><Permits /></Protected>} />
      <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
      <Route path="/reports" element={<Protected><Reports /></Protected>} />
      <Route path="/customer" element={<Protected><CustomerPortal /></Protected>} />
      <Route path="/documents" element={<Protected><Documents /></Protected>} />
      <Route path="/quality" element={<Protected><Quality /></Protected>} />
      <Route path="/energy" element={<Protected><Energy /></Protected>} />
      <Route path="/vendors" element={<Protected roles={["super_admin","company_admin","site_manager","store_incharge"]}><Vendors /></Protected>} />
      <Route path="/audit" element={<Protected roles={["super_admin","company_admin","site_manager"]}><AuditTrail /></Protected>} />
      <Route path="/users" element={<Protected roles={["super_admin","company_admin"]}><Users /></Protected>} />
      <Route path="/settings" element={<Protected roles={["super_admin","company_admin"]}><Settings /></Protected>} />
      <Route path="/master" element={<Protected roles={["super_admin","company_admin"]}><MasterHome /></Protected>} />
      <Route path="/master/:key" element={<Protected roles={["super_admin","company_admin"]}><MasterList /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrandingProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </BrandingProvider>
    </AuthProvider>
  );
}
